import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Volume2, Bookmark, BookmarkCheck, Calendar, Heart, LogOut, Settings, BookOpen, Flame, Share2, Play, Pause, ChevronRight } from 'lucide-react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import TreeLoadingSpinner from '@/components/TreeLoadingSpinner';
import TreeLogo from '@/components/TreeLogo';
import { useFeatureTracking, FEATURES } from '@/hooks/useAnalytics';
import { useTheme } from '@/context/ThemeContext';

// Get current season based on month
const getCurrentSeason = () => {
  const month = new Date().getMonth();
  if (month >= 2 && month <= 4) return 'spring';
  if (month >= 5 && month <= 7) return 'summer';
  if (month >= 8 && month <= 10) return 'autumn';
  return 'winter';
};

// Animated Seasonal Growing Branch Component
const SeasonalGrowingBranch = ({ className, style, delay = 0, direction = 'left', scale = 1, angle = 0 }) => {
  const isLeft = direction === 'left';
  const currentSeason = getCurrentSeason();
  
  // Calculate rotation angle - branches grow diagonally
  const rotationAngle = isLeft ? (angle || -25) : (angle || 25);
  
  const seasonColors = {
    spring: { trunk: 'hsl(30 35% 35%)', branch: 'hsl(30 35% 40%)', leaf: 'hsl(340 70% 75%)', leafAlt: 'hsl(100 40% 55%)' },
    summer: { trunk: 'hsl(30 40% 32%)', branch: 'hsl(30 40% 38%)', leaf: 'hsl(120 45% 40%)', leafAlt: 'hsl(100 50% 45%)' },
    autumn: { trunk: 'hsl(25 45% 30%)', branch: 'hsl(25 45% 35%)', leaf: 'hsl(25 80% 50%)', leafAlt: 'hsl(0 70% 55%)' },
    winter: { trunk: 'hsl(220 15% 35%)', branch: 'hsl(220 15% 40%)', leaf: 'hsl(200 30% 85%)', leafAlt: 'hsl(210 25% 90%)' }
  };
  
  const colors = seasonColors[currentSeason];
  
  return (
    <svg 
      className={`absolute pointer-events-none ${className}`}
      style={{ 
        ...style, 
        transform: `scale(${scale}) ${isLeft ? '' : 'scaleX(-1)'} rotate(${rotationAngle}deg)`,
        transformOrigin: isLeft ? 'bottom left' : 'bottom right'
      }}
      width="250" 
      height="350" 
      viewBox="0 0 250 350"
    >
      <path d="M30 350 Q25 280 40 220 Q55 160 35 110 Q15 60 45 20"
        stroke={colors.trunk} strokeWidth="5" fill="none" strokeLinecap="round"
        style={{ strokeDasharray: 500, strokeDashoffset: 500, animation: `growBranch 5s ease-out ${delay}s forwards` }}
      />
      <path d="M40 220 Q70 195 95 215 Q120 235 140 210"
        stroke={colors.branch} strokeWidth="3" fill="none" strokeLinecap="round"
        style={{ strokeDasharray: 180, strokeDashoffset: 180, animation: `growBranch 3s ease-out ${delay + 1.2}s forwards` }}
      />
      <path d="M35 110 Q70 85 100 110 Q130 135 155 110"
        stroke={colors.branch} strokeWidth="3" fill="none" strokeLinecap="round"
        style={{ strokeDasharray: 200, strokeDashoffset: 200, animation: `growBranch 3s ease-out ${delay + 1.8}s forwards` }}
      />
      <circle cx="140" cy="210" r="4" fill={colors.leaf}
        style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 3.5}s forwards` }}
      />
      <circle cx="155" cy="110" r="4" fill={colors.leaf}
        style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 3.8}s forwards` }}
      />
      <circle cx="95" cy="215" r="3" fill={colors.leafAlt}
        style={{ opacity: 0, animation: `fadeInLeaf 1.2s ease-out ${delay + 4}s forwards` }}
      />
    </svg>
  );
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dayParam = searchParams.get('day');
  const { trackFeature } = useFeatureTracking();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const [devotional, setDevotional] = useState(null);
  const [journalEntry, setJournalEntry] = useState('');
  const [existingEntry, setExistingEntry] = useState(null);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const audioRef = useRef(null); // Use ref instead of state
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [streak, setStreak] = useState({ current_streak: 0, longest_streak: 0 });
  const [missedDays, setMissedDays] = useState({ missed_count: 0, missed_devotionals: [] });

  // Function to stop and cleanup audio
  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = '';
      audioRef.current = null;
    }
    setIsPlayingAudio(false);
  };

  useEffect(() => {
    loadData();
  }, [dayParam]); // Reload when day parameter changes

  // Cleanup audio on unmount
  useEffect(() => {
    return () => {
      stopAudio();
    };
  }, []);

  const loadData = async () => {
    try {
      // Stop any playing audio when loading new devotional
      stopAudio();

      const [userRes, streakRes] = await Promise.all([
        axios.get(`${API}/auth/me`, { withCredentials: true }),
        axios.get(`${API}/user/streak`, { withCredentials: true })
      ]);

      setUser(userRes.data);
      setStreak(streakRes.data);

      // Load specific devotional or today's
      let devRes;
      if (dayParam) {
        devRes = await axios.get(`${API}/devotionals/${dayParam}`, { withCredentials: true });
      } else {
        devRes = await axios.get(`${API}/devotionals/today`, { withCredentials: true });
      }
      
      setDevotional(devRes.data);

      // Load missed devotionals
      try {
        const missedRes = await axios.get(`${API}/devotionals/missed`, { withCredentials: true });
        setMissedDays(missedRes.data);
      } catch (error) {
        console.error('Failed to load missed devotionals');
      }

      const [entryRes, bookmarksRes] = await Promise.all([
        axios.get(`${API}/journal/${devRes.data.devotional_id}`, { withCredentials: true }),
        axios.get(`${API}/bookmarks`, { withCredentials: true })
      ]);

      if (entryRes.data) {
        setExistingEntry(entryRes.data);
        setJournalEntry(entryRes.data.response);
      }

      const bookmarked = bookmarksRes.data.some(b => b.devotional_id === devRes.data.devotional_id);
      setIsBookmarked(bookmarked);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Subscription required');
        navigate('/subscribe');
      } else {
        toast.error('Failed to load devotional');
      }
    } finally {
      setLoading(false);
    }
  };

  const playAudio = async () => {
    // If audio is currently playing, pause it
    if (isPlayingAudio && audioRef.current) {
      audioRef.current.pause();
      setIsPlayingAudio(false);
      trackFeature(FEATURES.AUDIO_PAUSE);
      return;
    }

    // If audio exists but is paused, resume it
    if (audioRef.current && !isPlayingAudio) {
      audioRef.current.play();
      setIsPlayingAudio(true);
      trackFeature(FEATURES.AUDIO_PLAY, { resumed: true });
      return;
    }

    // Load and play new audio
    try {
      // Stop and cleanup any existing audio first
      stopAudio();

      toast.loading('Generating audio...', { id: 'audio-loading' });

      const response = await axios.get(
        `${API}/devotionals/${devotional.devotional_id}/audio`,
        { withCredentials: true, responseType: 'blob' }
      );

      toast.dismiss('audio-loading');

      const audioBlob = new Blob([response.data], { type: 'audio/mpeg' });
      const audioUrl = URL.createObjectURL(audioBlob);
      const audioElement = new Audio(audioUrl);

      audioElement.onended = () => {
        setIsPlayingAudio(false);
        URL.revokeObjectURL(audioUrl);
      };

      audioElement.onerror = () => {
        toast.error('Failed to play audio');
        setIsPlayingAudio(false);
        URL.revokeObjectURL(audioUrl);
      };

      await audioElement.play();
      audioRef.current = audioElement;
      setIsPlayingAudio(true);
      trackFeature(FEATURES.AUDIO_PLAY, { devotional_id: devotional.devotional_id });
      toast.success('Audio loaded');
    } catch (error) {
      toast.dismiss('audio-loading');
      toast.error('Failed to load audio');
    }
  };

  const saveJournalEntry = async () => {
    if (!journalEntry.trim()) {
      toast.error('Please write something first');
      return;
    }

    try {
      await axios.post(
        `${API}/journal`,
        { devotional_id: devotional.devotional_id, response: journalEntry },
        { withCredentials: true }
      );
      trackFeature(FEATURES.JOURNAL_SAVE, { devotional_id: devotional.devotional_id });
      toast.success('Journal entry saved');
    } catch (error) {
      toast.error('Failed to save entry');
    }
  };

  const toggleBookmark = async () => {
    try {
      if (isBookmarked) {
        await axios.delete(`${API}/bookmarks/${devotional.devotional_id}`, { withCredentials: true });
        setIsBookmarked(false);
        trackFeature(FEATURES.BOOKMARK_REMOVE, { devotional_id: devotional.devotional_id });
        toast.success('Bookmark removed');
      } else {
        await axios.post(`${API}/bookmarks?devotional_id=${devotional.devotional_id}`, {}, { withCredentials: true });
        setIsBookmarked(true);
        trackFeature(FEATURES.BOOKMARK_ADD, { devotional_id: devotional.devotional_id });
        toast.success('Bookmarked');
      }
    } catch (error) {
      toast.error('Failed to update bookmark');
    }
  };

  const handleLogout = async () => {
    stopAudio(); // Stop audio before logging out
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      toast.success('Logged out');
      navigate('/');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const handleShare = async () => {
    if (!devotional?.devotional_id) {
      toast.error('Unable to share at this time');
      return;
    }

    const shareUrl = `${window.location.origin}/share/${devotional.devotional_id}`;
    const shareText = `${devotional.liturgical_day} - Rooted Devotional`;
    
    // Try native share first (mobile)
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareText,
          text: `Join me in today's devotional reflection`,
          url: shareUrl
        });
        trackFeature(FEATURES.SHARE_CLICK, { method: 'native_share', devotional_id: devotional.devotional_id });
        toast.success('Shared successfully');
        return;
      } catch (error) {
        // User cancelled or error - fall through to clipboard
        if (error.name === 'AbortError') {
          return; // User cancelled, don't show error
        }
      }
    }
    
    // Fallback: Try modern clipboard API
    try {
      await navigator.clipboard.writeText(shareUrl);
      trackFeature(FEATURES.SHARE_CLICK, { method: 'clipboard', devotional_id: devotional.devotional_id });
      toast.success('Share link copied to clipboard!');
    } catch (clipboardError) {
      // Final fallback: Create temporary input element
      try {
        const tempInput = document.createElement('input');
        tempInput.value = shareUrl;
        tempInput.style.position = 'fixed';
        tempInput.style.opacity = '0';
        document.body.appendChild(tempInput);
        tempInput.select();
        tempInput.setSelectionRange(0, 99999); // For mobile
        document.execCommand('copy');
        document.body.removeChild(tempInput);
        trackFeature(FEATURES.SHARE_CLICK, { method: 'fallback', devotional_id: devotional.devotional_id });
        toast.success('Share link copied to clipboard!');
      } catch (fallbackError) {
        // If all else fails, show the URL in a toast
        toast.info(`Share this link: ${shareUrl}`, { duration: 10000 });
      }
    }
  };

  const copyToClipboard = (text) => {
    // This function is no longer needed but keeping for reference
    navigator.clipboard.writeText(text);
    toast.success('Share link copied to clipboard!');
  };

  const getSeasonClass = (season) => {
    const seasonMap = {
      'Lent': 'season-lent',
      'Advent': 'season-advent',
      'Christmas': 'season-christmas',
      'Epiphany': 'season-epiphany',
      'Holy Week': 'season-holy-week',
      'Easter': 'season-easter',
      'Ordinary Time': 'season-ordinary'
    };
    return seasonMap[season] || 'season-ordinary';
  };

  // Prevent right-click on protected content
  const handleContextMenu = (e) => {
    e.preventDefault();
    return false;
  };

  // Prevent keyboard shortcuts for copying
  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && (e.key === 'c' || e.key === 'p' || e.key === 's')) {
      e.preventDefault();
      return false;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <TreeLoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen relative overflow-hidden" onKeyDown={handleKeyDown}>
      {/* Animated Seasonal Growing Branches - Background */}
      <SeasonalGrowingBranch 
        direction="left" 
        delay={0.3}
        scale={1.1}
        className="opacity-[0.08] fixed"
        style={{ left: '-60px', bottom: '-80px', zIndex: 0 }}
      />
      <SeasonalGrowingBranch 
        direction="right" 
        delay={0.8}
        scale={1.1}
        className="opacity-[0.08] fixed"
        style={{ right: '-60px', bottom: '-80px', zIndex: 0 }}
      />
      
      {/* Print Warning */}
      <div className="print-warning">
        <p>This content is protected and cannot be printed.</p>
        <p>Please visit rootedfaithjournal.com to access your devotionals.</p>
      </div>
      
      {/* Navigation - Slate Gray Header */}
      <nav className="sticky top-0 z-50" style={{ backgroundColor: 'hsl(220 15% 55%)' }}>
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: 'hsl(15 40% 82%)' }}>
              <TreeLogo size={28} className="text-slate-600" />
            </div>
            <div>
              <h1 className="text-xl text-white font-light" style={{ fontFamily: 'Playfair Display, serif' }}>
                Rooted
              </h1>
              <p className="text-xs uppercase tracking-wider" style={{ color: 'hsl(45 50% 75%)' }}>Daily Devotional</p>
            </div>
          </div>
          
          {/* Navigation Items */}
          <div className="flex items-center gap-1">
            {/* Today - Active State */}
            <Button 
              variant="ghost" 
              size="sm" 
              className="rounded-full px-4"
              style={{ backgroundColor: 'hsl(15 40% 82%)', color: 'hsl(220 20% 30%)' }}
              onClick={() => { stopAudio(); navigate('/dashboard'); }}
              data-testid="nav-today-btn"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
              </svg>
              Today
            </Button>
            
            <Button variant="ghost" size="sm" className="rounded-full px-4 text-white/80 hover:text-white hover:bg-white/10" onClick={() => { stopAudio(); navigate('/calendar'); }} data-testid="nav-calendar-btn">
              <Calendar className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Calendar
            </Button>
            <Button variant="ghost" size="sm" className="rounded-full px-4 text-white/80 hover:text-white hover:bg-white/10" onClick={() => { stopAudio(); navigate('/bookmarks'); }} data-testid="nav-bookmarks-btn">
              <Bookmark className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Saved
            </Button>
            <Button variant="ghost" size="sm" className="rounded-full px-4 text-white/80 hover:text-white hover:bg-white/10" onClick={() => { stopAudio(); navigate('/journal'); }} data-testid="nav-journal-btn">
              <BookOpen className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Journal
            </Button>
            <Button variant="ghost" size="sm" className="rounded-full px-4 text-white/80 hover:text-white hover:bg-white/10" onClick={() => { stopAudio(); navigate('/settings'); }} data-testid="nav-settings-btn">
              <Settings className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Settings
            </Button>
            
            {/* Streak indicator */}
            {streak.current_streak > 0 && (
              <div className="flex items-center gap-1 px-3 py-1 ml-2 rounded-full" style={{ backgroundColor: 'hsl(15 40% 82%)' }} data-testid="streak-indicator">
                <Flame className="w-4 h-4" style={{ color: 'hsl(220 20% 30%)' }} strokeWidth={2} />
                <span className="text-sm font-medium" style={{ color: 'hsl(220 20% 30%)' }}>{streak.current_streak}</span>
              </div>
            )}
            
            <Button variant="ghost" size="sm" className="rounded-full text-white/80 hover:text-white hover:bg-white/10 ml-2" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4" strokeWidth={1.5} />
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-6 py-12">
        <div className="fade-in">
          {/* Day Card - Soft gray-lavender with decorative circles */}
          <div 
            className="relative rounded-3xl p-10 mb-10 overflow-hidden"
            style={{ backgroundColor: isDark ? 'hsl(250 12% 28%)' : 'hsl(250 12% 72%)' }}
            data-testid="day-card"
          >
            {/* Decorative circles - muted tones */}
            <div className="absolute top-1/4 left-1/4 w-32 h-32 rounded-full opacity-15" style={{ backgroundColor: isDark ? 'hsl(250 15% 40%)' : 'hsl(250 15% 60%)' }}></div>
            <div className="absolute bottom-1/4 right-1/3 w-24 h-24 rounded-full opacity-12" style={{ backgroundColor: isDark ? 'hsl(15 30% 45%)' : 'hsl(15 30% 75%)' }}></div>
            <div className="absolute top-1/2 right-1/4 w-20 h-20 rounded-full opacity-10" style={{ backgroundColor: isDark ? 'hsl(45 40% 45%)' : 'hsl(45 40% 75%)' }}></div>
            
            <div className="relative z-10 text-center">
              <p className="text-sm uppercase tracking-widest mb-4" style={{ color: isDark ? 'hsl(250 15% 75%)' : 'hsl(250 10% 40%)' }} data-testid="day-counter">
                Day {devotional.day_number} of 365
              </p>
              <h2 className="text-5xl text-white mb-3" style={{ fontFamily: 'Playfair Display, serif' }} data-testid="liturgical-day">
                {new Date().toLocaleDateString('en-US', { weekday: 'long' })}
              </h2>
              <p className="text-lg text-white/90" data-testid="devotional-date">
                {devotional.date}, {new Date().getFullYear()}
              </p>
            </div>
          </div>

          {/* Season Badge */}
          <div className={`inline-flex items-center px-4 py-2 rounded-full mb-6 ${getSeasonClass(devotional.season)}`}
               style={{ backgroundColor: 'hsl(var(--season-color) / 0.15)', color: 'hsl(var(--season-color))' }}
               data-testid="season-badge">
            <span className="text-sm font-medium">{devotional.season} • {devotional.liturgical_day}</span>
          </div>

          {/* Actions */}
          <div className="flex gap-2 mb-8">
            <Button
              data-testid="audio-play-btn"
              size="sm"
              className="rounded-full"
              style={{ backgroundColor: 'hsl(15 40% 82%)', color: 'hsl(220 20% 30%)' }}
              onClick={playAudio}
            >
              {isPlayingAudio ? (
                <>
                  <Pause className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Pause
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Play Audio
                </>
              )}
            </Button>
            <Button
              data-testid="bookmark-btn"
              variant="outline"
              size="sm"
              className="rounded-full border-2"
              style={{ borderColor: 'hsl(250 12% 80%)', color: 'hsl(250 15% 45%)' }}
              onClick={toggleBookmark}
            >
              {isBookmarked ? (
                <BookmarkCheck className="w-4 h-4 mr-2" strokeWidth={1.5} />
              ) : (
                <Bookmark className="w-4 h-4 mr-2" strokeWidth={1.5} />
              )}
              {isBookmarked ? 'Saved' : 'Save'}
            </Button>
            <Button
              data-testid="share-btn"
              variant="outline"
              size="sm"
              className="rounded-full border-2"
              style={{ borderColor: 'hsl(250 12% 80%)', color: 'hsl(250 15% 45%)' }}
              onClick={handleShare}
            >
              <Share2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Share
            </Button>
          </div>

          {/* Scripture - Protected Content */}
          <Card 
            className="p-8 mb-8 rounded-2xl border-0 shadow-sm protected-content" 
            style={{ backgroundColor: isDark ? 'hsl(220 15% 18%)' : 'hsl(45 30% 96%)' }} 
            data-testid="scripture-card"
            onContextMenu={handleContextMenu}
          >
            <p className="scripture-text" style={{ color: isDark ? 'hsl(45 30% 92%)' : 'hsl(220 15% 35%)' }}>
              {devotional.scripture}
            </p>
          </Card>

          {/* Reflection - Protected Content */}
          <section className="mb-8 protected-content" data-testid="reflection-section" onContextMenu={handleContextMenu}>
            <h3 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: isDark ? 'hsl(45 30% 95%)' : 'hsl(220 15% 30%)' }}>Reflection</h3>
            <p className="leading-relaxed whitespace-pre-line" style={{ color: isDark ? 'hsl(220 15% 85%)' : 'hsl(220 10% 40%)' }}>
              {devotional.reflection}
            </p>
          </section>

          {/* Writing Prompt */}
          <section className="mb-8" data-testid="writing-prompt-section">
            <h3 className="text-2xl mb-4 protected-content" style={{ fontFamily: 'Playfair Display, serif', color: isDark ? 'hsl(45 30% 95%)' : 'hsl(220 15% 30%)' }} onContextMenu={handleContextMenu}>Writing Prompt</h3>
            <p className="mb-4 leading-relaxed protected-content" style={{ color: isDark ? 'hsl(220 15% 85%)' : 'hsl(220 10% 40%)' }} onContextMenu={handleContextMenu}>
              {devotional.writing_prompt}
            </p>
            
            <Textarea
              data-testid="journal-textarea"
              placeholder="Write your response here..."
              value={journalEntry}
              onChange={(e) => setJournalEntry(e.target.value)}
              rows={6}
              className="mb-4 rounded-xl border-2"
              style={{ borderColor: 'hsl(80 20% 80%)' }}
            />
            
            <Button
              data-testid="save-journal-btn"
              onClick={saveJournalEntry}
              className="rounded-full"
              style={{ backgroundColor: 'hsl(250 15% 65%)', color: 'white' }}
            >
              Save Response
            </Button>
          </section>

          {/* Prayer - Protected Content */}
          <section className="mb-12 protected-content" data-testid="prayer-section" onContextMenu={handleContextMenu}>
            <h3 className="text-2xl mb-4" style={{ fontFamily: 'Playfair Display, serif', color: isDark ? 'hsl(45 30% 95%)' : 'hsl(220 15% 30%)' }}>Prayer</h3>
            <Card className="p-8 rounded-2xl border-0" style={{ backgroundColor: isDark ? 'hsl(250 12% 22%)' : 'hsl(250 12% 90%)' }}>
              <p className="scripture-text leading-relaxed whitespace-pre-line" style={{ color: isDark ? 'hsl(250 20% 90%)' : 'hsl(250 12% 35%)' }}>
                {devotional.prayer}
              </p>
            </Card>
          </section>

          {/* Missed Days Section */}
          {missedDays.missed_count > 0 && (
            <Card className="p-6 rounded-2xl border-0 mb-8" style={{ backgroundColor: 'hsl(15 50% 95%)' }} data-testid="missed-days-card">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'hsl(15 45% 85%)' }}>
                  <Calendar className="w-6 h-6" style={{ color: 'hsl(15 45% 55%)' }} strokeWidth={1.5} />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl mb-2 font-medium" style={{ fontFamily: 'Playfair Display, serif', color: 'hsl(220 15% 30%)' }}>
                    Did you miss a day?
                  </h3>
                  <p className="mb-4" style={{ color: 'hsl(220 10% 45%)' }}>
                    You have <strong>{missedDays.missed_count}</strong> unread devotional{missedDays.missed_count > 1 ? 's' : ''} from previous days. 
                    Take time to catch up and complete your spiritual journey.
                  </p>
                  
                  {missedDays.missed_devotionals.length > 0 && (
                    <div className="space-y-2 mb-4">
                      {missedDays.missed_devotionals.slice(0, 3).map((missed) => (
                        <button
                          key={missed.devotional_id}
                          onClick={() => navigate(`/dashboard?day=${missed.day_number}`)}
                          className="w-full text-left p-3 rounded-xl bg-white hover:shadow-md transition-all flex items-center justify-between group"
                          style={{ border: '1px solid hsl(15 30% 85%)' }}
                          data-testid={`missed-day-${missed.day_number}`}
                        >
                          <div>
                            <p className="font-medium text-sm" style={{ color: 'hsl(220 15% 30%)' }}>{missed.liturgical_day}</p>
                            <p className="text-xs" style={{ color: 'hsl(220 10% 50%)' }}>{missed.date} • Day {missed.day_number}</p>
                          </div>
                          <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" style={{ color: 'hsl(15 45% 55%)' }} strokeWidth={1.5} />
                        </button>
                      ))}
                    </div>
                  )}

                  <Button
                    variant="outline"
                    onClick={() => navigate('/calendar')}
                    className="rounded-full border-2"
                    style={{ borderColor: 'hsl(15 45% 65%)', color: 'hsl(15 45% 55%)' }}
                    data-testid="view-all-missed-btn"
                  >
                    View All Past Devotionals
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
};

export default DashboardPage;
